// Native +CURA Studio footer. Quiet, compact, built from the app's own
// Tailwind + brand tokens — no nhsuk-react-components dependency, so it
// reads as part of Studio rather than lifted from Cura Core.

import { Link } from "react-router";
import { reopenCookieBanner } from "@/components/CookieBanner";

const supportEmail = import.meta.env.VITE_SUPPORT_EMAIL || "evolve@thisisrdi.com";
const companyName = import.meta.env.VITE_COMPANY_NAME || "RDi Evolve";
const appVersion = import.meta.env.VITE_APP_VERSION || "0.1.0";
declare const __GIT_SHA__: string;

// One shared treatment for every legal item. Most navigate (anchors); the
// cookies item runs a function.
//
// The font/leading utilities are forced with `!` on purpose: brand.css has
// `[data-chrome] button { font: inherit }` (specificity 0,1,1) which the
// footer sits under, and it out-specifies a plain `.text-[13px]` utility
// (0,1,0). Without `!important` the <button> ignores the size class and
// renders at the inherited body size while the <a> links render at 13px —
// the exact mismatch this row kept showing. `!` makes both render
// identically regardless of the chrome reset.
const linkClass =
  "cursor-pointer rounded border-0 bg-transparent p-0 " +
  "!font-sans !text-[13px] !font-normal !leading-5 text-fg-3 " +
  "no-underline transition-colors hover:text-fg-1 " +
  "outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus)]";

type FooterLink =
  | { label: string; to: string; target?: string }
  | { label: string; onClick: () => void };

const links: FooterLink[] = [
  { label: "Contact us", to: `mailto:${supportEmail}` },
  { label: "Cookies", to: "/files/cookies.pdf", target: "_blank" },
  { label: "Cookies preferences", onClick: reopenCookieBanner },
  { label: "Privacy policy", to: "/files/privacy.pdf", target: "_blank" },
  { label: "Terms and conditions", to: "/files/terms.pdf", target: "_blank" },
];

export function Footer() {
  return (
    <footer
      data-chrome
      className="border-t border-[var(--border-1)] bg-white px-6 py-5"
    >
      <div className="mx-auto flex max-w-6xl flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <nav
          aria-label="Footer"
          className="flex flex-wrap items-center gap-x-5 gap-y-2"
        >
          {links.map((link) =>
            "onClick" in link ? (
              <button
                key={link.label}
                type="button"
                onClick={link.onClick}
                className={linkClass}
              >
                {link.label}
              </button>
            ) : (
              <Link
                key={link.label}
                className={linkClass}
                to={link.to}
                target={link.target}
              >
                {link.label}
              </Link>
            ),
          )}
        </nav>

        <div className="flex flex-col gap-1 text-[12px] text-fg-mute lg:items-end">
          <div className="text-fg-3">
            &copy; {new Date().getFullYear()} {companyName} &middot; +CURA Studio
          </div>
          <div className="flex items-center gap-2">
            <span>
              v{appVersion.slice(0, 7)} &middot; {__GIT_SHA__}
            </span>
            <span aria-hidden className="text-[var(--border-2)]">
              |
            </span>
            <span>
              <a
                href="https://thisisrdi.com"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-fg-3 no-underline hover:text-fg-1"
              >
                Evolve
              </a>
              <span aria-hidden className="px-1.5 text-[var(--border-2)]">
                |
              </span>
              <a
                href="https://thisisrdi.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-fg-3 no-underline hover:text-fg-1"
              >
                thisisrdi.com
              </a>
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
